import { AbstractService } from '@/services/core/serviceFactory';
import { transferWebSocketService } from '@/services/api';

export interface ESTransferPayload {
  global_flag: boolean;
  comment: string;
  pep_patient_id: number;
  provider_id: number;
  emr_id: string;
  first_name: string;
  last_name: string;
  address_line_1: string;
  city: string;
  state: string;
  zip_code: string;
  email_address: string;
  home_number: string;
  mobile_number: string;
  primary_insurance: string;
  primary_insurance_number: string;
  secondary_insurance: string;
  secondary_insurance_number: string;
  program_eligibility: string[];
  group_id: number;
  deferred_queue: boolean;
}

export interface ESTransferResponse {
  success: boolean;
  transfer_id?: number;
  message?: string;
}

/**
 * ES Patient Transfer Service - Handles REST API calls for patient transfer operations
 * Segregated from ES services to features folder for reusability
 * Uses proper three-layer architecture: GraphQL services + REST apiClient + dedicated WebSocket service
 */
export class ESPatientTransferService extends AbstractService {
  /**
   * Transfer patient to Care Specialist via REST API using patient_id
   */
  async transferPatientToCS(
    userId: string,
    patientId: number,
    payload: ESTransferPayload
  ): Promise<ESTransferResponse> {
    this.requireAuth();

    try {
      console.log('ESPatientTransferService: Transferring patient to CS');
      const response = await this.apiClient.post(
        `/api/pep/${userId}/transferToCs/${patientId}`,
        payload,
        this.context.token
      );

      if (response.success && response.data) {
        return {
          success: true,
          transfer_id: response.data.transfer_id,
          message: response.data.message || 'Patient transferred successfully'
        };
      }

      return {
        success: false,
        message: response.error || 'Transfer failed'
      };
    } catch (error) {
      console.error('ESPatientTransferService: Error transferring patient:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Transfer failed'
      };
    }
  }

  /**
   * Invalidate transfer via REST API
   */
  async invalidateTransfer(userId: string, transferId: number): Promise<ESTransferResponse> {
    this.requireAuth();

    try {
      console.log('ESPatientTransferService: Invalidating transfer');
      const response = await this.apiClient.put(
        `/api/pep/${userId}/invalidateTransfer/${transferId}`,
        {},
        this.context.token
      );

      if (response.success) {
        return {
          success: true,
          message: 'Transfer invalidated successfully'
        };
      } else {
        return {
          success: false,
          message: response.error || 'Failed to invalidate transfer'
        };
      }
    } catch (error) {
      console.error('ESPatientTransferService: Error invalidating transfer:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to invalidate transfer'
      };
    }
  }

  /**
   * Start transfer monitoring using dedicated WebSocket service
   */
  async startTransferMonitoring(
    transferId: number,
    onTransferComplete: (careSpecialist: string) => void,
    onError?: (error: string) => void,
    timeout?: number
  ): Promise<void> {
    this.requireAuth();

    try {
      console.log('ESPatientTransferService: Starting transfer monitoring');
      await transferWebSocketService.startTransferMonitoring({
        transferId,
        hasuraToken: this.context.hasuraToken!,
        onTransferComplete,
        onError,
        timeout
      });
    } catch (error) {
      console.error('ESPatientTransferService: Error starting transfer monitoring:', error);
      onError?.(error instanceof Error ? error.message : 'Failed to start transfer monitoring');
    }
  }

  /**
   * Stop transfer monitoring
   */
  stopTransferMonitoring(): void {
    console.log('ESPatientTransferService: Stopping transfer monitoring');
    transferWebSocketService.stopTransferMonitoring();
  }

  /**
   * Check if transfer monitoring is active
   */
  get isTransferMonitoringActive(): boolean {
    return transferWebSocketService.isActive;
  }

  /**
   * Create transfer payload from patient data
   */
  createTransferPayload(
    patientData: any,
    comment: string,
    groupId: number,
    patientId: number,
    globalFlag: boolean = false
  ): ESTransferPayload {
    return {
      global_flag: globalFlag,
      comment: comment.trim(),
      pep_patient_id: patientId,
      provider_id: patientData.provider_id,
      emr_id: patientData.emr_id || "",
      first_name: patientData.first_name || "",
      last_name: patientData.last_name || "",
      address_line_1: patientData.address_line_1 || "",
      city: patientData.city || "",
      state: patientData.state || "",
      zip_code: patientData.zip_code || "",
      email_address: patientData.email || "",
      home_number: patientData.home_phone_number || "",
      mobile_number: patientData.mobile_phone_number || "",
      primary_insurance: patientData.primary_insurance_provider || "",
      primary_insurance_number: patientData.primary_insurance_id || "",
      secondary_insurance: patientData.secondary_insurance_provider || "",
      secondary_insurance_number: patientData.secondary_insurance_id || "",
      program_eligibility: ["CCM"],
      group_id: groupId,
      deferred_queue: false
    };
  }

  /**
   * Validate transfer payload
   */
  validateTransferPayload(payload: ESTransferPayload): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    // Check required fields
    if (!payload.comment || payload.comment.trim().length === 0) {
      errors.push('Comment is required for transfer');
    }

    if (!payload.first_name || payload.first_name.trim().length === 0) {
      errors.push('Patient first name is required');
    }

    if (!payload.last_name || payload.last_name.trim().length === 0) {
      errors.push('Patient last name is required');
    }

    if (!payload.mobile_number || payload.mobile_number.trim().length === 0) {
      errors.push('Patient mobile number is required');
    }

    if (!payload.email_address || payload.email_address.trim().length === 0) {
      errors.push('Patient email is required');
    }

    if (!payload.pep_patient_id || payload.pep_patient_id <= 0) {
      errors.push('Valid patient ID is required');
    }

    if (!payload.group_id || payload.group_id <= 0) {
      errors.push('Valid group ID is required');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Check if transfer is enabled based on status and comment
   */
  isTransferEnabled(selectedStatus: string, comment: string): boolean {
    return selectedStatus === 'Consent' && comment.trim().length > 0;
  }

  /**
   * Format patient data for transfer display
   */
  formatPatientForTransfer(patientData: any): {
    name: string;
    phone: string;
    email: string;
    address: string;
  } {
    return {
      name: `${patientData.first_name || ''} ${patientData.last_name || ''}`.trim(),
      phone: this.formatPhoneNumber(patientData.mobile_phone_number || patientData.home_phone_number || ''),
      email: patientData.email || 'N/A',
      address: `${patientData.address_line_1 || ''}, ${patientData.city || ''}, ${patientData.state || ''} ${patientData.zip_code || ''}`.replace(/^,\s*|,\s*$/g, '')
    };
  }

  /**
   * Transfer patient to another user during call (via HBoxCall)
   * Used when a call is being transferred between users
   */
  async transferPatientOnCall(
    userId: string,
    patientId: string,
    assignedUserId: string
  ): Promise<ESTransferResponse> {
    this.requireAuth();

    try {
      console.log('ESPatientTransferService: Transferring patient on call');
      const response = await this.apiClient.post(
        `/api/ngpep/${userId}/enrollmentTransfer/${patientId}`,
        {
          enrollmentPatientId: patientId,
          assignedUserId: assignedUserId
        },
        this.context.token
      );

      if (response.success) {
        return {
          success: true,
          message: response.data?.message || 'Patient transferred successfully during call'
        };
      }

      return {
        success: false,
        message: response.error || 'Failed to transfer patient during call'
      };
    } catch (error) {
      console.error('ESPatientTransferService: Error transferring patient on call:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to transfer patient during call'
      };
    }
  }

  /**
   * Format phone number for display
   */
  private formatPhoneNumber(phoneNumber: string): string {
    if (!phoneNumber || phoneNumber === 'n/a') {
      return 'N/A';
    }
    
    // Remove all non-digits
    const digits = phoneNumber.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX if 10 digits
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    
    return phoneNumber;
  }
}

export const esPatientTransferService = new ESPatientTransferService();